import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import omit from 'lodash/omit';
import upperFirst from 'lodash/upperFirst';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import {
    BB_BETFEED_FILTER_CSS_CLASS_BLOCK_NAME,
    BB_BETFEED_FILTER_CSS_CLASS_HEAD_NAME,
    BB_BETFEED_FILTER_CSS_CLASS_BODY_NAME,
    BB_BETFEED_FILTER_CSS_CLASS_YELLOW_TEXT,
    BB_BETFEED_FILTER_BOX_CSS_CLASS_BLOCK_NAME,
    BB_BETFEED_FILTER_BOX_ACTIVE_CSS_CLASS_NAME_IDENTIFIER,
    BB_BETFEED_FILTER_BOX_SELECTED_CSS_CLASS_NAME_IDENTIFIER,
    BB_BETFEED_FILTER_BOX_CELL_CSS_CLASS_BLOCK_NAME,
    BB_BETFEED_FILTER_BOX_CELL_EMPTY_CSS_CLASS_BLOCK_NAME,
    BB_BETFEED_FILTER_BOX_BLANK_CELL_KEY_NAME,
    BB_BETFEED_FILTER_TYPE_SORT_BY,
    BB_BETFEED_FILTER_TYPE_SPORTS,
    BB_BETFEED_FILTER_TYPE_BET_TYPE,
    BB_BETFEED_FILTER_TYPE_EVENT_STATUS,
    GTM_BETFEED_PAGE,
    GTM_BETFEED_FILTER_SUFFIX,
    GTM_BETFEED_BET_TYPE_FILTER,
    GTM_BETFEED_SORT_BY_FILTER,
    GTM_BETFEED_BET_TYPE_FILTER_PREFIX,
    GTM_BETFEED_EVENT_STATUS_FILTER_PREFIX,
    GTM_BETFEED_SPLIT_FIRST,
    BB_BETFEED_FILTER_DEBOUNCE,
} from '../../constants';
import SportFilter from './SportFilter';
import BetTypeFilter from './BetTypeFilter';
import LabelContainer from './LabelContainer';

export class Filter extends React.Component {

    constructor(props) {
        super(props);

        // fetch will be triggered after 500 ms without any filter change
        this.refresh = debounce(this.props.refreshBets, BB_BETFEED_FILTER_DEBOUNCE);
        this.toggleFilter = this.toggleFilter.bind(this);
        this.handleToggleActive = this.handleToggleActive.bind(this);
        this.handleToggleFilter = this.handleToggleFilter.bind(this);
    }

    static propTypes = {
        filters: PropTypes.object.isRequired,
        labels: PropTypes.object.isRequired,
        active: PropTypes.string.isRequired,
        selected: PropTypes.object.isRequired,
        refreshBets: PropTypes.func.isRequired,
        activeFilter: PropTypes.func.isRequired,
        selectFilterOption: PropTypes.func.isRequired,
        clearFiltersHandler: PropTypes.func.isRequired,
        gtmAttribute: PropTypes.string,
    };

    static defaultProps = {
        gtmAttribute: null,
    };

    static funcProps = ['refreshBets', 'activeFilter', 'selectFilterOption', 'clearFiltersHandler'];

    shouldComponentUpdate(nextProps) {
        return !isEqual(
            omit(nextProps, Filter.funcProps),
            omit(this.props, Filter.funcProps),
        );
    }

    componentWillUnmount() {
        this.toggleActive(this.props.active);
    }

    render() {
        return <div className={BB_BETFEED_FILTER_CSS_CLASS_BLOCK_NAME}>
            {this.renderFilter()}
            {this.renderFilterOptions()}
            {this.renderFilterLabels()}
        </div>;
    }

    renderFilterLabels() {
        return (
            <div className={BB_BETFEED_FILTER_CSS_CLASS_BODY_NAME}>
                <LabelContainer
                    selected={this.props.selected}
                    toggleFilter={this.toggleFilter}
                />
            </div>
        );
    }

    handleToggleActive(type) {
        return () => {
            this.toggleActive(type);
        };
    }

    /**
     * Render all filter components according to store.state.filter.filters
     * Active filter according to store.state.filter.active
     *
     * @returns {Object} filter components
     */
    renderFilter() {

        return <div className={BB_BETFEED_FILTER_CSS_CLASS_HEAD_NAME}>
            {Object.keys(this.props.filters).map((type) => {
                const className = `${BB_BETFEED_FILTER_BOX_CSS_CLASS_BLOCK_NAME} ${BB_BETFEED_FILTER_CSS_CLASS_BLOCK_NAME}-${type} ${this.props.active === type ?
                    ` ${BB_BETFEED_FILTER_BOX_ACTIVE_CSS_CLASS_NAME_IDENTIFIER}` : ''}`;
                const cellBodyClass = !this.props.filters[type] ?
                    `${BB_BETFEED_FILTER_BOX_CELL_CSS_CLASS_BLOCK_NAME} ${BB_BETFEED_FILTER_BOX_CELL_EMPTY_CSS_CLASS_BLOCK_NAME}` :
                    BB_BETFEED_FILTER_BOX_CELL_CSS_CLASS_BLOCK_NAME;

                const gtmFilter = this.props.labels[type].split('.').splice(GTM_BETFEED_SPLIT_FIRST).join('').replace('_', '-');

                return <div key={type} className={className} onClick={this.handleToggleActive(type)} data-spectate-product={`${GTM_BETFEED_PAGE};${gtmFilter}${GTM_BETFEED_FILTER_SUFFIX}`}>
                    <div className={cellBodyClass}>
                        <Trans>{this.props.labels[type]}</Trans>
                        {this.renderFilterChildren(type)}
                    </div>
                </div>;
            })}
        </div>;
    }

    toggleActive(type) {
        this.props.activeFilter(type);
    }

    /**
     * Render specific styles or layout of filter based on the different types
     *
     * @param {string} type filter type, e.g., sports, betType, sortBy
     * @returns {Object} filter children
     */
    renderFilterChildren(type) {
        if (!this.props.filters[type]) {
            return null;
        }

        let children;
        const { sortBy, sports } = this.props.selected;

        if (type === BB_BETFEED_FILTER_TYPE_SPORTS.id) {
            const counter = Object.keys(sports).length;
            if (counter) {
                children = <span className={BB_BETFEED_FILTER_CSS_CLASS_YELLOW_TEXT}>{`(${counter})`}</span>;
            }
        } else if (type === BB_BETFEED_FILTER_TYPE_SORT_BY.id) {
            children = (
                <React.Fragment>
                    {': '}
                    <Trans>{sortBy.name}</Trans>
                </React.Fragment>
            );
        }

        return children;
    }

    /**
     * Proxy method for calling filter's options rendering methods dynamically
     * Real rendering method name is `render${filter type(first letter upper case)}`
     * e.g., renderSports, renderBetType, renderSortBy
     *
     * @returns {Object} filter options
     */
    renderFilterOptions() {
        if (!this.props.active) {
            return null;
        }

        const renderFunc = `render${upperFirst(this.props.active)}`;

        return <div className={BB_BETFEED_FILTER_CSS_CLASS_BODY_NAME}>
            {isFunction(this[renderFunc]) && this[renderFunc]()}
        </div>;
    }

    renderSports() {
        return <SportFilter
            sportFilter={this.props.filters.sports}
            fillBlankCell={this.fillBlankCell}
            toggleFilter={this.toggleFilter}
            selected={this.props.selected.sports}
            clearFiltersHandler={this.props.clearFiltersHandler}
            gtmAttribute={this.props.gtmAttribute}
            />;
    }

    renderBetType() {
        return (
            <React.Fragment>
                <BetTypeFilter
                    betType={this.props.filters.betType.betType}
                    selected={this.props.selected.betType}
                    toggleFilter={this.toggleFilter}
                    filterType={BB_BETFEED_FILTER_TYPE_BET_TYPE}
                    gtmAttribute={`${this.props.gtmAttribute};${GTM_BETFEED_BET_TYPE_FILTER};${GTM_BETFEED_BET_TYPE_FILTER_PREFIX}`} />
                <BetTypeFilter
                    betType={this.props.filters.betType.eventStatus}
                    selected={this.props.selected.eventStatus}
                    toggleFilter={this.toggleFilter}
                    filterType={BB_BETFEED_FILTER_TYPE_EVENT_STATUS}
                    gtmAttribute={`${this.props.gtmAttribute};${GTM_BETFEED_BET_TYPE_FILTER};${GTM_BETFEED_EVENT_STATUS_FILTER_PREFIX}`} />
            </React.Fragment>
        );

    }

    handleToggleFilter(filter, id) {
        return () => {
            this.toggleFilter(filter, id);
        };
    }

    /**
     * Render filter options for sortBy
     * Bind clicking method for fetching sorted bet cards
     *
     * @returns {Object} filter options for sortBy
     */
    renderSortBy() {
        const sortByFilter = this.props.filters[this.props.active] || [];
        const filterKeys = Object.keys(sortByFilter);

        const sortBy = filterKeys.map((id) => {
            const item = sortByFilter[id];
            const className = `${BB_BETFEED_FILTER_BOX_CSS_CLASS_BLOCK_NAME}${this.props.selected.sortBy.id === id ?
                ` ${BB_BETFEED_FILTER_BOX_SELECTED_CSS_CLASS_NAME_IDENTIFIER}` : ''}`;

            const gtmSortByFilterName = item.name.split('.').splice(GTM_BETFEED_SPLIT_FIRST).join('').replace('_', '-');

            return <div key={id} className={className} onClick={this.handleToggleFilter(BB_BETFEED_FILTER_TYPE_SORT_BY.id, id)} data-spectate-product={`${this.props.gtmAttribute};${GTM_BETFEED_SORT_BY_FILTER};${gtmSortByFilterName}`}>
                <div className={BB_BETFEED_FILTER_BOX_CELL_CSS_CLASS_BLOCK_NAME}>
                    <Trans>{item.name}</Trans>
                </div>
            </div>;
        });
        this.fillBlankCell(sortBy);

        return sortBy;
    }

    fillBlankCell(filterOptions) {
        const even = 2;
        if (filterOptions.length % even) {
            filterOptions.push(<div key={BB_BETFEED_FILTER_BOX_BLANK_CELL_KEY_NAME} className={BB_BETFEED_FILTER_BOX_CSS_CLASS_BLOCK_NAME}></div>);
        }
    }

    /**
     * Toggle the filter options
     * @param {string} filter Type of filter (sports/betType/sortBy)
     * @param {string} id of filter options from key of store.state.filter.filters
     * @returns {Object} filter options for sortBy
     */
    toggleFilter(filter, id) {
        this.props.selectFilterOption(filter, id);
        this.refresh();
    }
}

export default Filter;
